import { MapPin } from "lucide-react";
import type { NeighborhoodPoi } from "@shared/schema";

type NeighborhoodProps = {
  /** Display address e.g. "1117 Budapest, Példa utca 12." */
  address?: string | null;
  /** Geocoded / admin-refined coordinates. Map renders only when both set. */
  lat?: number | null;
  lng?: number | null;
  /** Admin-curated nearby points of interest. */
  pois?: NeighborhoodPoi[];
  /** Free / rich text describing the neighborhood. */
  description?: string | null;
  /** Title for the section header */
  title?: string;
};

function hasCoords(lat?: number | null, lng?: number | null): lat is number {
  return (
    typeof lat === "number" &&
    typeof lng === "number" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng)
  );
}

/**
 * "Környék és elhelyezkedés" — three independent blocks rendered in a fixed
 * order: interactive map → POI list → text description. Each block is only
 * rendered when it has content, and the whole section disappears when all
 * three are empty. The map is a lightweight OpenStreetMap embed (no JS map
 * library on the public bundle) and is fully responsive.
 */
export function Neighborhood({
  address,
  lat,
  lng,
  pois = [],
  description,
  title = "Környék és elhelyezkedés",
}: NeighborhoodProps) {
  const showMap = hasCoords(lat, lng);
  const showPois = pois.length > 0;
  const desc = description?.trim() ?? "";
  const showDesc = desc.length > 0;

  // Nothing to show → render nothing (acceptance: empty section is hidden).
  if (!showMap && !showPois && !showDesc) return null;

  // ~600 m bounding box around the marker.
  const dLat = 0.006;
  const dLng = 0.012;
  const bbox = showMap
    ? `${(lng! - dLng).toFixed(4)},${(lat - dLat).toFixed(4)},${(lng! + dLng).toFixed(4)},${(lat + dLat).toFixed(4)}`
    : "";
  const mapSrc = showMap
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat.toFixed(5)},${lng!.toFixed(5)}`
    : "";

  return (
    <section>
      <h2 className="text-lg font-semibold uppercase tracking-widish text-brand-700 mb-1 flex items-center gap-2">
        <MapPin size={20} className="text-accent" />
        {title}
      </h2>
      {address && (
        <p className="text-sm text-brand-500 mb-5">
          {address}
        </p>
      )}

      <div className="space-y-6">
        {/* 1 — Interactive map */}
        {showMap && (
          <div className="relative aspect-[16/10] sm:aspect-[16/9] bg-brand-100 border border-brand-200">
            <iframe
              src={mapSrc}
              className="absolute inset-0 w-full h-full"
              title="Helyszín térkép"
              loading="lazy"
            />
            <a
              href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=16/${lat}/${lng}`}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-2 right-2 bg-white/90 hover:bg-white text-[10px] uppercase tracking-widish font-semibold text-brand-700 px-2.5 py-1.5"
            >
              Megnyitás térképen ↗
            </a>
          </div>
        )}

        {/* 2 — POI list */}
        {showPois && (
          <div className="bg-brand-50 border border-brand-100 p-5 lg:p-6">
            <div className="text-[11px] uppercase tracking-widish font-semibold text-brand-400 mb-3">
              Közeli helyek
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {pois.map((p, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-sm text-brand-700"
                >
                  <span
                    className="w-8 h-8 grid place-items-center bg-white border border-brand-200 text-base shrink-0"
                    aria-hidden="true"
                  >
                    {p.icon?.trim() ? p.icon : "📍"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-brand-500">
                      {p.category}
                      {p.distance ? ` · ${p.distance}` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 3 — Text description */}
        {showDesc && (
          <p className="text-brand-600 leading-relaxed whitespace-pre-line">
            {desc}
          </p>
        )}
      </div>
    </section>
  );
}
